import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react'
import { useTranslation } from 'react-i18next'
import { ColDef, RowClassRules } from 'ag-grid-community'
import { ValueFormatterParams } from 'ag-grid-enterprise'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { selectUser } from '@/features/user/user.slice'
import { useLazyGetPositionsPaginatedQuery } from '@/apis/position/position-api.api'
import { POSITION_LIST_COLUMN_STATE_KEY } from '@/utils/constants'
import type {
  GridRef,
  InfiniteGridFetchParams,
} from '@/common/ag-grid/grid.types'
import { useHighlightRow } from '@/common/ag-grid/hooks/use-grid-setting'
import { CustomCellRendererProps } from 'ag-grid-react'
import { IdeaDirectionTag } from '@/pages/idea-list/components/idea-list-table/idea-direction-tag'
import { selectDashboardInput } from '@/redux/slices/dashboard-input.slice'
import {
  checkStateColoredNumberCellStyle,
  currencyValueFormatter,
  percentageValueFormatter,
} from '@/utils/formatters'
import { isDisabledIdeaState } from '@/enums/management-input.enum'
import { InfiniteGrid } from '@/common/ag-grid/infinite-grid'
import { setIdeaListDateRange } from '@/redux/slices/idea-list-input.slice'
import { isHistoricalEndDate } from '@/utils/time'
import { TickerCodeCellRenderer } from '@/common/ag-grid/ticker-code-cell-renderer'
import { IdeaTotalPnlCell } from '@/pages/idea-list/components/idea-list-table/idea-total-pnl-cell'
import type { PositionItem } from '@/apis/position/position-api.def'

/** Re-export for consumers that reference the table ref type */
export type PositionListTableRef = GridRef

// Mapping from ag-grid colId to backend orderBy field
const positionSortMapping: Record<string, string> = {
  tickerCode: 'TickerCode',
  tickerName: 'TickerName',
  localName: 'LocalName',
  direction: 'Direction',
  dailyReturnPct: 'DailyReturnPct',
  quarterlyReturnPct: 'QuarterlyReturnPct',
  totalReturnPct: 'TotalReturnPct',
  dailyPnl: 'DailyPnl',
  quarterPnl: 'QuarterPnl',
  totalPnl: 'TotalPnl',
  cumulativePnl: 'CumulativePnl',
  avgCost: 'AvgCost',
  shares: 'Shares',
  targetMarketValue: 'TargetMarketValue',
  currentMarketValue: 'CurrentMarketValue',
  pctBP: 'PctBP',
  pctGMV: 'PctGMV',
  state: 'State',
  contributor: 'Contributor',
  firmName: 'FirmName',
}

interface PositionListTableProps {
  onRefreshed?: () => void
  height: number
  /** Selected contributor id. When provided, positions are fetched for this user. */
  contributor?: string
  /** When true, the ticker code column is rendered as plain text without the navigation link. */
  disableTickerCodeLink?: boolean
  onViewHistoryIdea: (id: string) => void
  ref?: React.Ref<GridRef>
}

export function PositionListTable({
  onRefreshed,
  height,
  contributor,
  disableTickerCodeLink = false,
  onViewHistoryIdea,
  ref,
}: PositionListTableProps) {
  const { t } = useTranslation()
  const gridRef = useRef<GridRef>(null)
  useImperativeHandle(
    ref,
    () => ({
      refresh: () => gridRef.current?.refresh(),
      reload: () => gridRef.current?.reload(),
      getGridApi: () => gridRef.current?.getGridApi() ?? null,
    }),
    []
  )
  const userInfo = useAppSelector(selectUser)
  const dashboardInput = useAppSelector(selectDashboardInput)
  const dispatch = useAppDispatch()
  const [triggerGetPositions] = useLazyGetPositionsPaginatedQuery()

  // Shared row-highlight behavior (predicate + auto-clear of router state)
  const { isHighlightedRow } = useHighlightRow()

  // region Column Definitions

  // When viewing a historical end date, point-in-time metrics are shown as '-'
  const isHistorical = isHistoricalEndDate(dashboardInput.dateRange[1])

  const columnDefs = useMemo<ColDef[]>(() => {
    const baseColumns: ColDef[] = [
      {
        headerName: t('idea_list.idea_table.columns.ticker_code'),
        field: 'tickerCode',
        ...(disableTickerCodeLink
          ? {}
          : {
              cellRenderer: TickerCodeCellRenderer,
              cellRendererParams: {
                tooltipTitle: t('idea_list.idea_table.ticker_code_tooltip'),
                navigateTo: '/idea',
                hasAccess: true,
                getNavigationState: (params: CustomCellRendererProps) => ({
                  activeIdeaId: params.data.ideaId,
                }),
                onBeforeNavigate: () => {
                  const [start, end] = dashboardInput.dateRange
                  dispatch(setIdeaListDateRange([start ?? null, end ?? null]))
                },
                isNavigationDisabled: (params: CustomCellRendererProps) =>
                  params.data.state !== 'Active',
              },
            }),
      },
      {
        headerName: t('idea_list.idea_table.columns.ticker_name'),
        field: 'tickerName',
      },
      {
        headerName: t('idea_list.idea_table.columns.ticker_legal_name'),
        field: 'localName',
      },
      {
        headerName: t('idea_list.idea_table.columns.direction'),
        field: 'direction',
        cellRenderer: (props: CustomCellRendererProps) => {
          if (props.value !== undefined) {
            return <IdeaDirectionTag direction={props.data.direction} />
          }
          return <></>
        },
      },
      {
        headerName: t('idea_list.idea_table.columns.daily_return_pct'),
        field: 'dailyReturnPct',
        type: 'numberColumn',
        valueFormatter: (params: ValueFormatterParams) =>
          isHistorical ? '-' : percentageValueFormatter(params),
        cellStyle: isHistorical ? undefined : checkStateColoredNumberCellStyle,
      },
      {
        headerName: t('idea_list.idea_table.columns.quarterly_return_pct'),
        field: 'quarterlyReturnPct',
        type: 'numberColumn',
        valueFormatter: (params: ValueFormatterParams) =>
          isHistorical ? '-' : percentageValueFormatter(params),
        cellStyle: isHistorical ? undefined : checkStateColoredNumberCellStyle,
      },
      {
        headerName: t('idea_list.idea_table.columns.total_return_pct'),
        field: 'totalReturnPct',
        type: 'numberColumn',
        valueFormatter: (params: ValueFormatterParams) =>
          isHistorical ? '-' : percentageValueFormatter(params),
        cellStyle: isHistorical ? undefined : checkStateColoredNumberCellStyle,
      },
      {
        headerName: t('idea_list.idea_table.columns.daily_pnl'),
        field: 'dailyPnl',
        type: 'numberColumn',
        valueFormatter: (params: ValueFormatterParams) =>
          currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
        cellStyle: checkStateColoredNumberCellStyle,
      },
      {
        headerName: t('idea_list.idea_table.columns.quarter_pnl'),
        field: 'quarterPnl',
        type: 'numberColumn',
        valueFormatter: (params: ValueFormatterParams) =>
          isHistorical
            ? '-'
            : currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
        cellStyle: isHistorical ? undefined : checkStateColoredNumberCellStyle,
      },
      {
        headerName: t('idea_list.idea_table.columns.current_total_pnl'),
        field: 'totalPnl',
        type: 'numberColumn',
        cellStyle: checkStateColoredNumberCellStyle,
        cellRenderer: IdeaTotalPnlCell,
        cellRendererParams: {
          onButtonClick: (rowData: PositionItem) => {
            onViewHistoryIdea(rowData.ideaId)
          },
        },
      },
      {
        headerName: t('idea_list.idea_table.columns.cumulative_pnl'),
        field: 'cumulativePnl',
        type: 'numberColumn',
        valueFormatter: (params: ValueFormatterParams) =>
          currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
        cellStyle: checkStateColoredNumberCellStyle,
      },
      {
        headerName: t('idea_list.idea_table.columns.avg_cost'),
        field: 'avgCost',
        type: 'numberColumn',
        valueFormatter: currencyValueFormatter,
      },
      {
        headerName: t('idea_list.idea_table.columns.shares'),
        field: 'shares',
        type: 'numberColumn',
      },
      {
        headerName: t('idea_list.idea_table.columns.target_market_value'),
        field: 'targetMarketValue',
        type: 'numberColumn',
        valueFormatter: (params: ValueFormatterParams) =>
          isHistorical
            ? '-'
            : currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
      },
      {
        headerName: t('idea_list.idea_table.columns.current_market_value'),
        field: 'currentMarketValue',
        type: 'numberColumn',
        valueFormatter: (params: ValueFormatterParams) =>
          currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
      },
      {
        headerName: t('idea_list.idea_table.columns.pct_bp'),
        field: 'pctBP',
        type: 'numberColumn',
        valueFormatter: (params: ValueFormatterParams) =>
          isHistorical ? '-' : percentageValueFormatter(params),
        cellStyle: isHistorical ? undefined : checkStateColoredNumberCellStyle,
      },
      {
        headerName: t('idea_list.idea_table.columns.pct_gmv'),
        field: 'pctGMV',
        type: 'numberColumn',
        valueFormatter: (params: ValueFormatterParams) =>
          isHistorical ? '-' : percentageValueFormatter(params),
        cellStyle: isHistorical ? undefined : checkStateColoredNumberCellStyle,
      },
      {
        headerName: t('idea_list.idea_table.columns.state'),
        field: 'state',
      },
      {
        headerName: t('idea_list.idea_table.columns.contributor'),
        field: 'contributor',
        hide: true,
      },
      {
        headerName: t('idea_list.idea_table.columns.firm_name'),
        field: 'firmName',
        hide: true,
      },
    ]

    return baseColumns
  }, [
    dashboardInput.dateRange,
    disableTickerCodeLink,
    dispatch,
    onViewHistoryIdea,
    t,
    isHistorical,
  ])

  // endregion

  // region Fetch Data

  const fetchData = useCallback(
    async (params: InfiniteGridFetchParams) => {
      // InfiniteGrid already translates colId to the backend field name via
      // sortMapping before calling fetchData, so use it directly here.
      const sortField = params.sortModel[0]?.colId
      const [start, end] = dashboardInput.dateRange

      const result = await triggerGetPositions({
        page: Math.floor(params.startRow / params.pageSize) + 1,
        pageSize: params.pageSize,
        orderBy: sortField || undefined,
        orderDirection: params.sortModel[0]?.sort?.toUpperCase(),
        filterColumn: params.filterColumn || undefined,
        filterText: params.filterText || undefined,
        includeLiquidated: dashboardInput.showLiquidatedIdeas,
        userId: contributor || userInfo.sub,
        startDate: start ?? undefined,
        endDate: end ?? undefined,
      }).unwrap()
      return { rows: result.data.positions, totalCount: result.data.total }
    },
    [
      triggerGetPositions,
      dashboardInput.showLiquidatedIdeas,
      dashboardInput.dateRange,
      contributor,
      userInfo.sub,
    ]
  )

  // endregion

  // region Row Class Rules

  const rowClassRules = useMemo<RowClassRules>(
    () => ({
      'ag-row--state-disabled': (params) =>
        isDisabledIdeaState(params.data?.state),
      'ag-row--hightlighted': isHighlightedRow,
    }),
    [isHighlightedRow]
  )

  // endregion

  // region External Query Refresh

  // The grid datasource is intentionally stable, so external query inputs
  // (date range, liquidated toggle, contributor) no longer reset the grid
  // implicitly. Reload silently when they change so stale rows from the
  // previous query are replaced. Uses reload() (not refresh()) so a changed
  // query always re-fetches instead of being coalesced into an in-flight
  // refresh. Depends on the serialized query value — not object identity — so
  // re-selecting the same query still reloads deterministically.
  const queryKey = JSON.stringify([
    dashboardInput.showLiquidatedIdeas,
    dashboardInput.dateRange,
    contributor,
    userInfo.sub,
  ])
  const isFirstQueryRunRef = useRef(true)
  useEffect(() => {
    if (isFirstQueryRunRef.current) {
      isFirstQueryRunRef.current = false
      return
    }
    gridRef.current?.reload()
  }, [queryKey])

  // endregion

  return (
    <InfiniteGrid
      ref={gridRef}
      columnDefs={columnDefs}
      storageKey={POSITION_LIST_COLUMN_STATE_KEY}
      fetchData={fetchData}
      sortMapping={positionSortMapping}
      height={height}
      onRefreshed={onRefreshed}
      rowClassRules={rowClassRules}
      autoRefreshInterval={Number(import.meta.env.VITE_GET_IDEA_LIST_INTERVAL)}
      mobileColumnOrder={[
        'tickerName',
        'pctBP',
        'dailyPnl',
        'totalReturnPct',
        'direction',
      ]}
      className={'idea-list-table'}
    />
  )
}

PositionListTable.displayName = 'PositionListTable'
